import { Field } from '@sitecore-jss/sitecore-jss-nextjs';
import { SiteMasterRouteFields } from '../SiteMaster/types';
import {
  getFieldValue,
  getMultilistDisplayNames,
  extractNumberFromString,
} from '../SiteMaster/utils';

export type GlanceMetric = {
  id: string;
  value: string;
  valueField: Field<string | number>;
  labelField: Field<string>;
};

const hasLabel = (field?: Field<string>): boolean => Boolean(getFieldValue(field).trim());

export const getPositiveCountDisplay = (field?: {
  value?: string | number | boolean | null;
}): string | null => {
  const raw = getFieldValue(field).trim();
  if (raw === '' || raw === '0') {
    return null;
  }

  return raw;
};

export const getYearsActiveCount = (
  raw?: string,
  nowYear = new Date().getFullYear()
): string | null => {
  const year = extractNumberFromString(raw);
  if (year === null || year < 1000 || year > 9999) {
    return null;
  }

  const yearsActive = nowYear - year;
  if (yearsActive <= 0) {
    return null;
  }
  return String(yearsActive);
};

export const getLanguagesSpokenCount = (routeFields: SiteMasterRouteFields): string | null => {
  const count = getMultilistDisplayNames(routeFields.siteSupportedLanguages).length;
  if (count <= 0) {
    return null;
  }
  return String(count);
};

export const getGlanceMetrics = (
  routeFields: SiteMasterRouteFields,
  fields: {
    activeStudiesText?: Field<string>;
    enrollingNowText?: Field<string>;
    yearsActiveText?: Field<string>;
    languagesSpokenText?: Field<string>;
  }
): GlanceMetric[] => {
  const yearsActive = getYearsActiveCount(getFieldValue(routeFields.siteEstablishmentYearText));
  const languagesSpoken = getLanguagesSpokenCount(routeFields);

  const rows: Array<{
    id: string;
    value: string | null;
    valueField: Field<string | number>;
    labelField?: Field<string>;
  }> = [
    {
      id: 'activeStudies',
      value: getPositiveCountDisplay(routeFields.activeStudiesCount),
      valueField: routeFields.activeStudiesCount ?? { value: '' },
      labelField: fields.activeStudiesText,
    },
    {
      id: 'enrollingNow',
      value: getPositiveCountDisplay(routeFields.recruitingNowCount),
      valueField: routeFields.recruitingNowCount ?? { value: '' },
      labelField: fields.enrollingNowText,
    },
    {
      id: 'yearsActive',
      value: yearsActive,
      // Derived from the establishment year, so it has no editable field of its own.
      valueField: { value: yearsActive ?? '' },
      labelField: fields.yearsActiveText,
    },
    {
      id: 'languagesSpoken',
      value: languagesSpoken,
      valueField: { value: languagesSpoken ?? '' },
      labelField: fields.languagesSpokenText,
    },
  ];
  return rows.filter(
    (row): row is GlanceMetric =>
      Boolean(row.value) && Boolean(row.labelField) && hasLabel(row.labelField)
  );
};
